import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { 
  Search, LayoutDashboard, Package, Layers, FlaskConical, Warehouse, 
  Calendar, DollarSign, Boxes, Users, FileText, Settings, BookOpen,
  BarChart2, Keyboard, Star, Clock
} from 'lucide-react';

const PAGES = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, keywords: ['inicio', 'home', 'painel'] },
  { path: '/recepcao', label: 'Recepção', icon: Package, keywords: ['receber', 'entrada', 'material'] },
  { path: '/montagem', label: 'Montagem', icon: Layers, keywords: ['montar', 'kit', 'preparar'] },
  { path: '/ciclos', label: 'Ciclos', icon: FlaskConical, keywords: ['esterilização', 'autoclave', 'processo'] },
  { path: '/arsenal', label: 'Arsenal', icon: Warehouse, keywords: ['estoque', 'armazenar', 'retirar'] },
  { path: '/agendamentos', label: 'Agendamentos', icon: Calendar, keywords: ['agendar', 'coleta', 'entrega'] },
  { path: '/financeiro', label: 'Financeiro', icon: DollarSign, keywords: ['caixa', 'fatura', 'contrato', 'dinheiro'] },
  { path: '/estoque', label: 'Estoque', icon: Boxes, keywords: ['produto', 'fornecedor', 'inventario'] },
  { path: '/clientes', label: 'Clientes', icon: Users, keywords: ['cliente', 'hospital', 'clinica'] },
  { path: '/materiais', label: 'Materiais', icon: FileText, keywords: ['material', 'instrumento', 'item'] },
  { path: '/autoclaves', label: 'Autoclaves', icon: Settings, keywords: ['equipamento', 'maquina'] },
  { path: '/pops', label: 'POPs', icon: BookOpen, keywords: ['procedimento', 'protocolo', 'documento'] },
  { path: '/relatorios', label: 'Relatórios', icon: BarChart2, keywords: ['relatorio', 'exportar', 'excel'] },
];

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  
  // Carregar favoritos do localStorage
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sterisul_favorites') || '[]');
    } catch {
      return [];
    }
  });
  
  // Carregar histórico recente
  const [recent, setRecent] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sterisul_recent') || '[]');
    } catch {
      return [];
    }
  });

  // Filtrar páginas baseado na busca
  const filteredPages = PAGES.filter(page => {
    const searchLower = search.toLowerCase();
    return page.label.toLowerCase().includes(searchLower) ||
           page.keywords.some(k => k.includes(searchLower));
  });

  // Páginas favoritas
  const favoritePages = PAGES.filter(p => favorites.includes(p.path));
  
  // Páginas recentes (não favoritas)
  const recentPages = PAGES.filter(p => recent.includes(p.path) && !favorites.includes(p.path)).slice(0, 3);

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Navegar para página
  const goToPage = (path) => {
    // Adicionar ao histórico recente
    const newRecent = [path, ...recent.filter(p => p !== path)].slice(0, 5);
    setRecent(newRecent);
    localStorage.setItem('sterisul_recent', JSON.stringify(newRecent));
    
    navigate(path);
    onOpenChange(false);
  };

  // Toggle favorito
  const toggleFavorite = (path, e) => {
    e.stopPropagation();
    const newFavorites = favorites.includes(path)
      ? favorites.filter(p => p !== path)
      : [...favorites, path];
    setFavorites(newFavorites);
    localStorage.setItem('sterisul_favorites', JSON.stringify(newFavorites));
  };

  // Navegação por teclado
  const handleKeyDown = (e) => {
    const items = search ? filteredPages : [...favoritePages, ...recentPages, ...PAGES];
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          goToPage(items[selectedIndex].path);
        }
        break;
      case 'Escape':
        onOpenChange(false);
        break;
      default:
        break;
    }
  };

  const renderPageItem = (page, index, showFavorite = true) => {
    const Icon = page.icon;
    const isFavorite = favorites.includes(page.path);
    
    return (
      <div
        key={page.path}
        onClick={() => goToPage(page.path)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
          selectedIndex === index 
            ? 'bg-blue-100 text-blue-900' 
            : 'hover:bg-slate-100'
        }`}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <Icon className="w-5 h-5 text-slate-500" />
        <span className="flex-1">{page.label}</span>
        {showFavorite && (
          <button
            onClick={(e) => toggleFavorite(page.path, e)}
            className={`p-1 rounded hover:bg-slate-200 ${isFavorite ? 'text-yellow-500' : 'text-slate-300'}`}
          >
            <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Search className="w-5 h-5 text-slate-400" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Buscar páginas... (Ctrl+K)"
            className="border-0 shadow-none focus-visible:ring-0 px-0"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 rounded">
            <Keyboard className="w-3 h-3" /> ESC
          </kbd>
        </div>
        
        <div className="max-h-80 overflow-y-auto p-2">
          {search ? (
            // Resultados da busca
            filteredPages.length > 0 ? (
              filteredPages.map((page, i) => renderPageItem(page, i))
            ) : (
              <div className="text-center text-slate-500 py-8">
                Nenhuma página encontrada
              </div>
            )
          ) : (
            // Favoritos + Recentes + Todas
            <>
              {favoritePages.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 px-3 py-1 text-xs font-medium text-slate-500 uppercase">
                    <Star className="w-3 h-3" /> Favoritos
                  </div>
                  {favoritePages.map((page, i) => renderPageItem(page, i))}
                </div>
              )}
              
              {recentPages.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 px-3 py-1 text-xs font-medium text-slate-500 uppercase">
                    <Clock className="w-3 h-3" /> Recentes
                  </div>
                  {recentPages.map((page, i) => renderPageItem(page, favoritePages.length + i))}
                </div>
              )}
              
              <div>
                <div className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">
                  Todas as Páginas
                </div>
                {PAGES.map((page, i) => renderPageItem(page, favoritePages.length + recentPages.length + i))}
              </div>
            </>
          )}
        </div>
        
        <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500 flex items-center gap-4">
          <span><kbd className="px-1 bg-white rounded border">↑↓</kbd> navegar</span>
          <span><kbd className="px-1 bg-white rounded border">Enter</kbd> selecionar</span>
          <span><kbd className="px-1 bg-white rounded border">⭐</kbd> favoritar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
